import React, { useState, useEffect, useRef } from "react";
import { IonPage, IonContent, IonButton, IonText, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const SkipPattern: React.FC = () => {
    const [sequence, setSequence] = useState<number[]>([]);
    const [missingNumber, setMissingNumber] = useState<number>(0);
    const [options, setOptions] = useState<number[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Skip Counting Worksheets for UKG | Fun Math Practice for Kids";
    }, []);

    const generateNewQuestion = () => {
        const start = Math.floor(Math.random() * 89) + 11; // Start from a random number between 1-10
        //const step = Math.floor(Math.random() * 3) + 1; // Step of 1, 2, or 3
        const step = Math.random() < 0.5 ? 2 : 5;
        const numbers = Array.from({ length: 5 }, (_, i) => start + i * step);
        const missingIndex = Math.floor(Math.random() * numbers.length);
        const missingValue = numbers[missingIndex];
        numbers[missingIndex] = "?" as any; // Placeholder for missing number

        setSequence(numbers);
        setMissingNumber(missingValue);

        // Generate options
        const wrongOption1 = missingValue + (Math.random() > 0.5 ? 1 : -1);
        const wrongOption2 = missingValue + (Math.random() > 0.5 ? 2 : -2);
        const optionsArray = [missingValue, wrongOption1, wrongOption2].sort(() => Math.random() - 0.5);
        setOptions(optionsArray);
        setMessage("");
    };

    const checkAnswer = (selected: number) => {
        if (selected === missingNumber) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setMessage("❌ Oops! Try again.");
        }
        setTimeout(generateNewQuestion, 1500);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach skip counting to UKG kids with engaging worksheets and activities. Download free printable exercises for 2s, 5s, and 10s at Mama’s Guidance." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/skip-counting" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Skip Counting</h1>

                                    <p>
                                        <strong>Skip counting</strong> is a fun and powerful way to teach early math concepts and prepare UKG children for multiplication. By learning to count in jumps (like 2s, 5s, or 10s), kids understand number patterns and improve mental math speed.
                                    </p>

                                    <h2>🔢 What is Skip Counting?</h2>
                                    <p>
                                        Skip counting means counting forward by numbers other than 1. For example:
                                    </p>
                                    <ul>
                                        <li><strong>By 2s:</strong> 2, 4, 6, 8, 10…</li>
                                        <li><strong>By 5s:</strong> 5, 10, 15, 20…</li>
                                        <li><strong>By 10s:</strong> 10, 20, 30, 40…</li>
                                    </ul>

                                    <p>
                                        These patterns help children understand groups, rows, and quantities – important for multiplication and time-telling skills.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand and identify number patterns</li>
                                        <li>Develop faster number recall and sequencing</li>
                                        <li>Build strong foundation for multiplication tables</li>
                                        <li>Improve mental math abilities</li>
                                    </ul>

                                    <h2>🖍️ Skip Counting Activities</h2>
                                    <ul>
                                        <li>Color the numbers that appear in 2s, 5s, or 10s</li>
                                        <li>Connect-the-dot skip counting pictures</li>
                                        <li>Fill in the blanks with missing skip-count numbers</li>
                                        <li>Match counting patterns to visual groups</li>
                                        <li>Maze games using skip count rules</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>👩‍🏫 Teaching Tips</h2>
                                    <ul>
                                        <li>Use fingers, toys, or beads to count in groups of 2, 5, or 10</li>
                                        <li>Play number jump games on the floor with chalk or tape</li>
                                        <li>Sing counting songs that follow skip counting patterns</li>
                                        <li>Use clocks to teach counting by 5s (minutes)</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Skip Counting</h1>

                                <p>
                                    <strong>Skip counting</strong> is a fun and powerful way to teach early math concepts and prepare UKG children for multiplication. By learning to count in jumps (like 2s, 5s, or 10s), kids understand number patterns and improve mental math speed.
                                </p>

                                <h2>🔢 What is Skip Counting?</h2>
                                <p>
                                    Skip counting means counting forward by numbers other than 1. For example:
                                </p>
                                <ul>
                                    <li><strong>By 2s:</strong> 2, 4, 6, 8, 10…</li>
                                    <li><strong>By 5s:</strong> 5, 10, 15, 20…</li>
                                    <li><strong>By 10s:</strong> 10, 20, 30, 40…</li>
                                </ul>

                                <p>
                                    These patterns help children understand groups, rows, and quantities – important for multiplication and time-telling skills.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand and identify number patterns</li>
                                    <li>Develop faster number recall and sequencing</li>
                                    <li>Build strong foundation for multiplication tables</li>
                                    <li>Improve mental math abilities</li>
                                </ul>

                                <h2>🖍️ Skip Counting Activities</h2>
                                <ul>
                                    <li>Color the numbers that appear in 2s, 5s, or 10s</li>
                                    <li>Connect-the-dot skip counting pictures</li>
                                    <li>Fill in the blanks with missing skip-count numbers</li>
                                    <li>Match counting patterns to visual groups</li>
                                    <li>Maze games using skip count rules</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>👩‍🏫 Teaching Tips</h2>
                                <ul>
                                    <li>Use fingers, toys, or beads to count in groups of 2, 5, or 10</li>
                                    <li>Play number jump games on the floor with chalk or tape</li>
                                    <li>Sing counting songs that follow skip counting patterns</li>
                                    <li>Use clocks to teach counting by 5s (minutes)</li>
                                </ul>
                            </article>
                            <IonText>
                                <h2>Find the Missing Number</h2>
                            </IonText>
                            <IonText>
                                <h1>{sequence.join(" ")}</h1>
                            </IonText>
                            <div style={{ display: 'flex', gap: '30px' }}>
                                {options.map((option, index) => (
                                    <div className="background-blue" style={{ width: 'auto', padding: '20px', fontSize: '30px' }} key={index} onClick={() => checkAnswer(option)}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            {message && <IonText><h3>{message}</h3></IonText>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default SkipPattern;
